/**
 * The tenant layer.
 *
 * ═══════════════════════════════════════════════════════════════════
 * NO COMPONENT IN THIS APPLICATION MAY NAME A CUSTOMER.
 *
 * Not in a heading, not in a page title, not in a colour, not in a label, not
 * in a placeholder. A screen that needs to say "Cost centre" asks for
 * `t.costCentre`; a screen that needs the organisation's name asks for
 * `branding.organisationName`. Both come from the server, which reads them from
 * one row of tenant configuration.
 *
 * That is what makes this one product rather than two applications that happen
 * to share a repository. If you find yourself typing a customer's name into a
 * component, the configuration is missing a field — add it there instead.
 * ═══════════════════════════════════════════════════════════════════
 *
 * Branding is fetched BEFORE sign-in, because the sign-in page has to know
 * whose sign-in page it is. That endpoint returns the public face only: name,
 * colours, strapline, and the demonstration banner. The full configuration —
 * which modules are enabled, which features are switched on — arrives with the
 * session.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';

export interface BrandingColours {
  brand900: string;
  brand800: string;
  brand700: string;
  brand600: string;
  brand500: string;
  brand100: string;
  brand50: string;
  accent700: string;
  accent600: string;
  accent100: string;
}

export interface Branding {
  organisationName: string;
  organisationShortName: string;
  productName: string;
  productTagline: string;
  applicationTitle: string;
  monogram: string;
  logoSvg: string | null;
  faviconEmoji: string;
  colours: BrandingColours;
  login?: { heading: string; intro: string; points: string[] };
  providerLine?: string;
}

export interface Terminology {
  organisation: string;
  organisationPlural: string;
  topLevel: string;
  entity: string;
  entityPlural: string;
  centralUnit: string;
  directorate: string;
  directoratePlural: string;
  department: string;
  departmentPlural: string;
  costCentre: string;
  costCentrePlural: string;
  project: string;
  projectPlural: string;
  budgetHolder: string;
  budgetHolderPlural: string;
  financeTeam: string;
  approver: string;
  requisition: string;
  requisitionPlural: string;
  purchaseOrder: string;
  purchaseOrderPlural: string;
  supplier: string;
  supplierPlural: string;
  customer: string;
  customerPlural: string;
  consolidated: string;
  entityLeader: string;
}

export interface TenantModules {
  [key: string]: boolean;
}

export interface TenantFeatures {
  [key: string]: boolean;
}

export interface TenantConfiguration {
  organisationId: string;
  profileKey: string;
  branding: Branding;
  terminology: Terminology;
  structure: Array<{ key: string; label: string; labelPlural: string; maps: string; levelType?: string; description?: string }>;
  modules: TenantModules;
  features: TenantFeatures;
  locale: { language: string; currency: string; dateFormat?: string; [k: string]: unknown };
  financial: {
    yearStartMonth: number;
    yearStartDay: number;
    approvalBands: number[];
    capitalisationThresholdPounds: number;
    defaultPaymentTermsDays: number;
    [k: string]: unknown;
  };
  reporting: { enabledReports: string[]; [k: string]: unknown };
  dashboard: { cards: string[]; centralCards: string[] };
  support: { deskName: string; email: string; telephone: string; hours: string; escalation: string };
  environmentLabel: string;
  isDemonstration: boolean;
}

interface BrandingResponse {
  branding: Branding;
  terminology?: Partial<Terminology>;
  environmentLabel: string;
  isDemonstration: boolean;
  configured: boolean;
}

interface TenantState {
  branding: Branding;
  terminology: Terminology;
  configuration: TenantConfiguration | null;
  environmentLabel: string;
  isDemonstration: boolean;
  ready: boolean;
  /** Pull the full configuration once a session exists. */
  loadConfiguration: () => Promise<void>;
  clearConfiguration: () => void;
  moduleEnabled: (key: string) => boolean;
  featureEnabled: (key: string) => boolean;
}

/**
 * Fallback terminology.
 *
 * Used only in the moment between the page loading and the server answering.
 * Every word here is generic on purpose: if a screen ever renders with these
 * values it must still read sensibly, not look broken.
 */
const NEUTRAL_TERMINOLOGY: Terminology = {
  organisation: 'Organisation',
  organisationPlural: 'Organisations',
  topLevel: 'Organisation',
  entity: 'Entity',
  entityPlural: 'Entities',
  centralUnit: 'Central',
  directorate: 'Directorate',
  directoratePlural: 'Directorates',
  department: 'Department',
  departmentPlural: 'Departments',
  costCentre: 'Cost centre',
  costCentrePlural: 'Cost centres',
  project: 'Project',
  projectPlural: 'Projects',
  budgetHolder: 'Budget holder',
  budgetHolderPlural: 'Budget holders',
  financeTeam: 'Finance',
  approver: 'Approver',
  requisition: 'Requisition',
  requisitionPlural: 'Requisitions',
  purchaseOrder: 'Purchase order',
  purchaseOrderPlural: 'Purchase orders',
  supplier: 'Supplier',
  supplierPlural: 'Suppliers',
  customer: 'Customer',
  customerPlural: 'Customers',
  consolidated: 'Consolidated',
  entityLeader: 'Chief executive',
};

const NEUTRAL_BRANDING: Branding = {
  organisationName: 'Altari Finance',
  organisationShortName: 'Altari',
  productName: 'Altari Finance',
  productTagline: 'Financial Management Platform',
  applicationTitle: 'Altari Finance',
  monogram: 'AF',
  logoSvg: null,
  faviconEmoji: '📘',
  colours: {
    brand900: '#0d2137',
    brand800: '#14304d',
    brand700: '#1c4066',
    brand600: '#245488',
    brand500: '#2f6ba8',
    brand100: '#dbe8f4',
    brand50: '#f0f6fb',
    accent700: '#8a4b08',
    accent600: '#b3610b',
    accent100: '#fdefdd',
  },
  providerLine: 'Powered by Altari Systems',
};

const TenantContext = createContext<TenantState | null>(null);

/**
 * Push the organisation's palette into the stylesheet.
 *
 * The whole application is coloured from CSS custom properties, so rebranding
 * is these ten assignments and nothing else — no component knows a hex value.
 */
function applyColours(colours: BrandingColours): void {
  const root = document.documentElement;
  const map: Array<[string, keyof BrandingColours]> = [
    ['--brand-900', 'brand900'],
    ['--brand-800', 'brand800'],
    ['--brand-700', 'brand700'],
    ['--brand-600', 'brand600'],
    ['--brand-500', 'brand500'],
    ['--brand-100', 'brand100'],
    ['--brand-50', 'brand50'],
    ['--accent-700', 'accent700'],
    ['--accent-600', 'accent600'],
    ['--accent-100', 'accent100'],
  ];
  for (const [property, key] of map) {
    if (colours[key]) root.style.setProperty(property, colours[key]);
  }
}

function applyDocumentIdentity(branding: Branding): void {
  document.title = branding.applicationTitle;
  if (branding.faviconEmoji) {
    // An emoji favicon keeps the deployment recognisable in a tab strip without
    // an asset pipeline, which matters when branding is configuration.
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
      `<text y="52" font-size="52">${branding.faviconEmoji}</text></svg>`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(NEUTRAL_BRANDING);
  const [terminology, setTerminology] = useState<Terminology>(NEUTRAL_TERMINOLOGY);
  const [configuration, setConfiguration] = useState<TenantConfiguration | null>(null);
  const [environmentLabel, setEnvironmentLabel] = useState('Demonstration Environment');
  const [isDemonstration, setIsDemonstration] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<BrandingResponse>('/platform/branding')
      .then((response) => {
        if (cancelled) return;
        setBranding(response.branding);
        setTerminology((current) => ({ ...current, ...response.terminology }));
        setEnvironmentLabel(response.environmentLabel);
        setIsDemonstration(response.isDemonstration);
        applyColours(response.branding.colours);
        applyDocumentIdentity(response.branding);
      })
      .catch(() => {
        // The sign-in page still renders, neutrally branded, if the server is
        // not yet up. It never shows a broken shell.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadConfiguration = useCallback(async () => {
    const response = await api.get<{ configuration: TenantConfiguration }>('/platform/config');
    setConfiguration(response.configuration);
    setBranding(response.configuration.branding);
    setTerminology(response.configuration.terminology);
    setEnvironmentLabel(response.configuration.environmentLabel);
    setIsDemonstration(response.configuration.isDemonstration);
    applyColours(response.configuration.branding.colours);
    applyDocumentIdentity(response.configuration.branding);
  }, []);

  const clearConfiguration = useCallback(() => setConfiguration(null), []);

  const value = useMemo<TenantState>(
    () => ({
      branding,
      terminology,
      configuration,
      environmentLabel,
      isDemonstration,
      ready,
      loadConfiguration,
      clearConfiguration,
      // A module or feature nobody has configured is OFF. Defaulting to on
      // would show a customer a module they have not bought.
      moduleEnabled: (key: string) => configuration?.modules?.[key] === true,
      featureEnabled: (key: string) => configuration?.features?.[key] === true,
    }),
    [branding, terminology, configuration, environmentLabel, isDemonstration, ready, loadConfiguration, clearConfiguration],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantState {
  const value = useContext(TenantContext);
  if (!value) throw new Error('useTenant must be used inside a TenantProvider.');
  return value;
}

/** Shorthand for the common case: `const t = useTerms();  t.costCentre` */
export function useTerms(): Terminology {
  return useTenant().terminology;
}

/** Sentence-case a term that a tenant supplied capitalised, for mid-sentence use. */
export function lower(term: string): string {
  return term.charAt(0).toLowerCase() + term.slice(1);
}
